import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Progress,
  Avatar,
  Tooltip,
  Spin,
  Collapse,
  Divider,
} from "antd";
import {
  FaDollarSign,
  FaSearch,
  FaUsers,
  FaLayerGroup,
  FaTasks,
  FaClock,
  FaFilter,
  FaChartBar,
  FaArrowLeft,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPauseCircle,
  FaUserTie,
  FaInfoCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate } from "react-router-dom";
import Api from "../../api/Api";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatMoney = (value) => {
  if (value == null || isNaN(value)) return "—";
  return new Intl.NumberFormat("pt-AO", {
    style: "currency",
    currency: "AOA",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatHoras = (horas) => {
  if (horas == null || isNaN(horas)) return "—";
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  return `${h}h ${m}min`;
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaCheckCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaClock /> },
  };
  return map[id_status] || { label: "—", color: "default", icon: null };
};

/* ─────────────────────────────────────────────────────────────────────────────
   CARD MÉTRICAS (reutilizável)
───────────────────────────────────────────────────────────────────────────── */
const MetricCard = ({ title, value, icon, iconBg, iconColor, subtitle }) => (
  <Card
    bordered={false}
    style={{
      borderRadius: 16,
      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
      height: "100%",
    }}
    bodyStyle={{ padding: "20px 24px" }}
  >
    <Space size="middle" align="start">
      <div
        style={{
          backgroundColor: iconBg,
          borderRadius: 12,
          padding: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { style: { fontSize: 22, color: iconColor } })}
      </div>
      <div>
        <Text style={{ fontSize: 13, color: "#8c8c8c", display: "block" }}>{title}</Text>
        <div style={{ fontSize: 26, fontWeight: 700, color: "#1f1f1f", lineHeight: 1.2, marginTop: 4 }}>
          {value}
        </div>
        {subtitle && (
          <Text style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4, display: "block" }}>
            {subtitle}
          </Text>
        )}
      </div>
    </Space>
  </Card>
);

/* ─────────────────────────────────────────────────────────────────────────────
   TELA PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
const CustoProjeto = () => {
  const { id } = useParams(); // id do projeto da URL
  const navigate = useNavigate();

  const [tarefas, setTarefas] = useState([]);       // dados brutos da API
  const [horasMap, setHorasMap] = useState({});      // { id_tarefa: data }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [etapaFilter, setEtapaFilter] = useState("TODAS");
  const [usuarioFilter, setUsuarioFilter] = useState("TODOS");

  /* ── Carregamento ── */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const lista = await Api.ListTarefaCusto({id_projeto:id});
      const arr = Array.isArray(lista) ? lista : [];
      setTarefas(arr);

      // buscar horas de cada tarefa única
      const uniqueIds = [...new Set(arr.map((t) => t.id_tarefa))];
      const results = await Promise.allSettled(
        uniqueIds.map((tid) => Api.ListHorasCusto({ id: tid }))
      );

      const map = {};
      results.forEach((res, i) => {
        if (res.status === "fulfilled" && res.value?.data) {
          map[uniqueIds[i]] = res.value.data;
        }
      });
      setHorasMap(map);
    } catch {
      toast.error("Erro ao carregar dados de custo.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
    document.title = "Custos do Projeto";
  }, [loadData]);

  /* ── Computação dos dados ── */
  // Monta lista enriquecida: uma linha por (tarefa × usuário)
  const enriched = tarefas.map((t) => {
    const horas = horasMap[t.id_tarefa];
    // usar minutos_trabalhados para evitar erro de ponto flutuante
    const horasExatas = (horas?.minutos_trabalhados ?? 0) / 60;
    const salario = parseFloat(t.salario_hora) || 0;
    const custo = salario * horasExatas;

    return {
      ...t,
      horas_trabalhadas: horasExatas,
      horas_por_user: horasExatas,
      formatado: horas?.formatado ?? "—",
      custo,
      minutos_suspensos: horas?.minutos_suspensos ?? 0,
    };
  });

  // opções de filtro
  const etapas = [...new Set(enriched.map((t) => t.etapa).filter(Boolean))];
  const usuarios = [...new Map(enriched.map((t) => [t.id_user, t.nome])).entries()];

  // aplica filtros
  const filtered = enriched.filter((t) => {
    const matchEtapa = etapaFilter === "TODAS" || t.etapa === etapaFilter;
    const matchUser = usuarioFilter === "TODOS" || String(t.id_user) === String(usuarioFilter);
    const q = search.toLowerCase();
    const matchSearch =
      !q || t.titulo?.toLowerCase().includes(q) || t.nome?.toLowerCase().includes(q);
    return matchEtapa && matchUser && matchSearch;
  });

  /* ── Métricas gerais (sem filtro) ── */
  const custoTotal = enriched.reduce((acc, t) => acc + (t.custo || 0), 0);
  const horasTotal = [...new Set(enriched.map((t) => t.id_tarefa))].reduce((acc, tid) => {
    return acc + (horasMap[tid]?.horas_trabalhadas ?? 0);
  }, 0);
  const tarefasUnicas = new Set(enriched.map((t) => t.id_tarefa)).size;

  /* ── Custo por etapa ── */
  const custoEtapa = etapas.map((etapa) => {
    const items = enriched.filter((t) => t.etapa === etapa);
    const total = items.reduce((a, t) => a + t.custo, 0);
    const horas = [...new Set(items.map((t) => t.id_tarefa))].reduce(
      (a, tid) => a + (horasMap[tid]?.minutos_trabalhados ?? 0) / 60, 0
    );
    const tarefasUnicas = [...new Map(items.map((t) => [t.id_tarefa, t.titulo])).entries()];
    return { etapa, total, horas, tarefasCount: tarefasUnicas.length, tarefasTitulos: tarefasUnicas };
  });

  /* ── Custo por usuário ── */
  const custoUsuario = usuarios.map(([uid, nome]) => {
    const items = enriched.filter((t) => t.id_user === uid);
    const total = items.reduce((a, t) => a + t.custo, 0);
    const horas = items.reduce((a, t) => a + t.horas_por_user, 0);
    const salario = parseFloat(items[0]?.salario_hora) || 0;
    const tarefasTitulos = [...new Map(items.map((t) => [t.id_tarefa, t.titulo])).entries()];
    return { id_user: uid, nome, total, horas, salario, tarefasCount: tarefasTitulos.length, tarefasTitulos };
  });

  /* ── Custo por tarefa (agrupado) ── */
  const custoTarefa = [...new Set(enriched.map((t) => t.id_tarefa))].map((tid) => {
    const items = enriched.filter((t) => t.id_tarefa === tid);
    const first = items[0];
    const totalCusto = items.reduce((a, t) => a + t.custo, 0);
    const horas = horasMap[tid]?.horas_trabalhadas ?? 0;
    const formatado = horasMap[tid]?.formatado ?? "—";
    return {
      id_tarefa: tid,
      titulo: first?.titulo,
      etapa: first?.etapa,
      id_status: first?.id_status,
      percentagem: first?.percentagem,
      responsaveis: horasMap[tid]?.responsaveis ?? [],
      horas,
      formatado,
      totalCusto,
    };
  });

  /* ── Coluna principal: por tarefa ── */
  const columnsTabela = [
    {
      title: "Tarefa",
      dataIndex: "titulo",
      key: "titulo",
      width: 220,
      render: (titulo, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            shape="square"
            size="default"
            style={{ backgroundColor: "#e6f4ff", color: "#1677ff", borderRadius: 8, flexShrink: 0 }}
            icon={<FaTasks />}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#1f1f1f" }}>{titulo}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.etapa}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "id_status",
      key: "status",
      width: 110,
      render: (id_status) => {
        const { label, color, icon } = getStatusInfo(id_status);
        return (
          <Tag
            color={color}
            style={{ borderRadius: 20, padding: "2px 10px", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}
          >
            {icon} {label}
          </Tag>
        );
      },
    },
    {
      title: "Progresso",
      dataIndex: "percentagem",
      key: "percentagem",
      width: 140,
      render: (v) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress percent={v || 0} size="small" status={v === 100 ? "success" : "active"} style={{ flex: 1 }} strokeColor={{ "0%": "#1677ff", "100%": "#52c41a" }} />
          <Text strong style={{ fontSize: 12 }}>{v || 0}%</Text>
        </div>
      ),
    },
    {
      title: "Horas trabalhadas",
      dataIndex: "formatado",
      key: "horas",
      width: 140,
      render: (v) => (
        <Space>
          <FaClock style={{ color: "#1677ff" }} />
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    {
      title: "Responsáveis",
      dataIndex: "responsaveis",
      key: "responsaveis",
      width: 180,
      render: (resp) => {
        if (!resp?.length) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={resp.map((r) => r.nome).join(", ")}>
            <Space>
              <Avatar.Group maxCount={3} size="small">
                {resp.map((r) => (
                  <Avatar key={r.id_user} style={{ backgroundColor: "#1677ff", fontSize: 10 }}>
                    {r.nome?.charAt(0)}
                  </Avatar>
                ))}
              </Avatar.Group>
              <Text style={{ fontSize: 12 }}>{resp.length} pessoa{resp.length > 1 ? "s" : ""}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: "Custo Total",
      dataIndex: "totalCusto",
      key: "totalCusto",
      width: 150,
      align: "right",
      sorter: (a, b) => a.totalCusto - b.totalCusto,
      render: (v) => (
        <Text strong style={{ color: v > 0 ? "#1677ff" : "#8c8c8c", fontSize: 14 }}>
          {formatMoney(v)}
        </Text>
      ),
    },
  ];

  /* ── Colunas tabela usuários ── */
  const columnsUser = [
    {
      title: "Colaborador",
      dataIndex: "nome",
      key: "nome",
      render: (nome) => (
        <Space>
          <Avatar style={{ backgroundColor: "#1677ff" }}>{nome?.charAt(0)}</Avatar>
          <Text strong>{nome}</Text>
        </Space>
      ),
    },
    {
      title: "Salário/hora",
      dataIndex: "salario",
      key: "salario",
      align: "right",
      render: (v) => <Text>{v > 0 ? formatMoney(v) : <Tag color="warning">Não definido</Tag>}</Text>,
    },
    {
      title: "Horas alocadas",
      dataIndex: "horas",
      key: "horas",
      align: "right",
      render: (v) => (
        <Space>
          <FaClock style={{ color: "#8c8c8c" }} />
          <Text>{formatHoras(v)}</Text>
        </Space>
      ),
    },
    {
      title: "Tarefas",
      key: "tarefas",
      render: (_, record) => (
        <Space size={4} wrap>
          {record.tarefasTitulos?.map(([tid, titulo]) => (
            <Tag key={tid} color="blue" style={{ borderRadius: 20, fontSize: 12 }}>{titulo}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Custo Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      sorter: (a, b) => a.total - b.total,
      render: (v) => (
        <Text strong style={{ color: v > 0 ? "#1677ff" : "#8c8c8c", fontSize: 14 }}>
          {formatMoney(v)}
        </Text>
      ),
    },
  ];

  /* ── Colunas tabela etapas ── */
  const columnsEtapa = [
    {
      title: "Etapa",
      dataIndex: "etapa",
      key: "etapa",
      render: (etapa) => (
        <Space>
          <Avatar shape="square" style={{ backgroundColor: "#722ed1", borderRadius: 8 }} icon={<FaLayerGroup />} />
          <Text strong>{etapa}</Text>
        </Space>
      ),
    },
    {
      title: "Tarefas",
      key: "tarefas",
      render: (_, record) => (
        <Space size={4} wrap>
          {record.tarefasTitulos?.map(([tid, titulo]) => (
            <Tag key={tid} color="purple" style={{ borderRadius: 20, fontSize: 12 }}>{titulo}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Horas totais",
      dataIndex: "horas",
      key: "horas",
      align: "right",
      render: (v) => (
        <Space>
          <FaClock style={{ color: "#8c8c8c" }} />
          <Text>{formatHoras(v)}</Text>
        </Space>
      ),
    },
    {
      title: "Custo Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      sorter: (a, b) => a.total - b.total,
      render: (v) => (
        <Text strong style={{ color: "#722ed1", fontSize: 14 }}>
          {formatMoney(v)}
        </Text>
      ),
    },
  ];

  // total dos filtrados (tabela de tarefas)
  const custoFiltrado = filtered.reduce((acc, t) => acc + (t.custo || 0), 0);
  const isFiltered =
    search || etapaFilter !== "TODAS" || usuarioFilter !== "TODOS";

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── Cabeçalho ── */}
        <div
          style={{
             background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(19, 194, 194, 0.2)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FaDollarSign style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Custos do Projeto
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                    Análise financeira baseada em horas trabalhadas
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                size="large"
                icon={<FaArrowLeft />}
                onClick={() => navigate(-1)}
                style={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  borderRadius: 30,
                  fontWeight: 500,
                }}
              >
                Voltar
              </Button>
            </Col>
          </Row>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Spin size="large" tip="Calculando custos..." />
          </div>
        ) : (
          <>
            {/* ── Cards métricas principais ── */}
            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Custo Total do Projeto"
                  value={formatMoney(custoTotal)}
                  icon={<FaDollarSign />}
                  iconBg="#e6fffb"
                  iconColor="#13c2c2"
                  subtitle={`${tarefasUnicas} tarefa${tarefasUnicas !== 1 ? "s" : ""} concluída${tarefasUnicas !== 1 ? "s" : ""}`}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Horas Trabalhadas"
                  value={formatHoras(horasTotal)}
                  icon={<FaClock />}
                  iconBg="#e6f4ff"
                  iconColor="#1677ff"
                  subtitle="Total nas tarefas"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Colaboradores"
                  value={usuarios.length}
                  icon={<FaUsers />}
                  iconBg="#f9f0ff"
                  iconColor="#722ed1"
                  subtitle="Com custo alocado"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Etapas"
                  value={etapas.length}
                  icon={<FaLayerGroup />}
                  iconBg="#fff7e6"
                  iconColor="#fa8c16"
                  subtitle={`${tarefasUnicas} tarefas no total`}
                />
              </Col>
            </Row>

            {/* ── Custo por Etapa ── */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
              title={
                <Space>
                  <FaLayerGroup style={{ color: "#722ed1" }} />
                  <span style={{ fontWeight: 600 }}>Custo por Etapa</span>
                </Space>
              }
            >
              <Table
                columns={columnsEtapa}
                dataSource={custoEtapa}
                rowKey="etapa"
                pagination={false}
                size="middle"
                locale={{ emptyText: "Sem dados" }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <Text strong style={{ color: "#722ed1", fontSize: 15 }}>
                          {formatMoney(custoTotal)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>

            {/* ── Custo por Usuário ── */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
              title={
                <Space>
                  <FaUserTie style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 600 }}>Custo por Colaborador</span>
                </Space>
              }
              extra={
                <Tooltip title="Colaboradores sem salário/hora cadastrado não são contabilizados no custo">
                  <FaInfoCircle style={{ color: "#8c8c8c", cursor: "pointer" }} />
                </Tooltip>
              }
            >
              <Table
                columns={columnsUser}
                dataSource={custoUsuario}
                rowKey="id_user"
                pagination={false}
                size="middle"
                locale={{ emptyText: "Sem dados" }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                      <Table.Summary.Cell index={0} colSpan={4}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <Text strong style={{ color: "#1677ff", fontSize: 15 }}>
                          {formatMoney(custoUsuario.reduce((a, u) => a + u.total, 0))}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>

            {/* ── Detalhe por tarefa (com filtros) ── */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
              title={
                <Space>
                  <FaTasks style={{ color: "#13c2c2" }} />
                  <span style={{ fontWeight: 600 }}>Custo por Tarefa</span>
                  {isFiltered && (
                    <Tag color="cyan">
                      Filtrado: {formatMoney(custoFiltrado)}
                    </Tag>
                  )}
                </Space>
              }
              extra={
                <Row gutter={12} align="middle">
                  <Col>
                    <Input
                      size="middle"
                      prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                      placeholder="Pesquisar..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                      style={{ borderRadius: 20, width: 200 }}
                    />
                  </Col>
                  <Col>
                    <Select
                      size="middle"
                      style={{ width: 160 }}
                      value={etapaFilter}
                      onChange={setEtapaFilter}
                      suffixIcon={<FaFilter style={{ color: "#722ed1" }} />}
                    >
                      <Option value="TODAS">Todas as etapas</Option>
                      {etapas.map((e) => (
                        <Option key={e} value={e}>{e}</Option>
                      ))}
                    </Select>
                  </Col>
                  <Col>
                    <Select
                      size="middle"
                      style={{ width: 160 }}
                      value={usuarioFilter}
                      onChange={setUsuarioFilter}
                      suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
                    >
                      <Option value="TODOS">Todos os colaboradores</Option>
                      {usuarios.map(([uid, nome]) => (
                        <Option key={uid} value={String(uid)}>{nome}</Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              }
              bodyStyle={{ padding: 0 }}
            >
              <Table
                columns={columnsTabela}
                dataSource={
                  // agrupamos filtered por id_tarefa para a tabela principal
                  [...new Set(filtered.map((t) => t.id_tarefa))].map((tid) => {
                    const items = filtered.filter((t) => t.id_tarefa === tid);
                    const first = items[0];
                    const totalCusto = items.reduce((a, t) => a + t.custo, 0);
                    return {
                      id_tarefa: tid,
                      titulo: first?.titulo,
                      etapa: first?.etapa,
                      id_status: first?.id_status,
                      percentagem: first?.percentagem,
                      responsaveis: horasMap[tid]?.responsaveis ?? [],
                      horas: horasMap[tid]?.horas_trabalhadas ?? 0,
                      formatado: horasMap[tid]?.formatado ?? "—",
                      totalCusto,
                    };
                  })
                }
                rowKey="id_tarefa"
                loading={loading}
                pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} tarefas`, style: { padding: "16px 24px" } }}
                locale={{ emptyText: "Nenhuma tarefa encontrada" }}
                scroll={{ x: 1000 }}
                expandable={{
                  expandedRowRender: (record) => {
                    // detalhe por usuário dentro da tarefa
                    const items = filtered.filter((t) => t.id_tarefa === record.id_tarefa);
                    return (
                      <div style={{ padding: "12px 24px", background: "#fafafa", borderRadius: 8 }}>
                        <Text strong style={{ fontSize: 13, color: "#8c8c8c", display: "block", marginBottom: 10 }}>
                          Custo por colaborador nesta tarefa
                        </Text>
                        <Row gutter={[12, 12]}>
                          {items.map((item) => (
                            <Col key={item.id_user} xs={24} sm={12} md={8}>
                              <Card
                                size="small"
                                bordered
                                style={{ borderRadius: 10 }}
                              >
                                <Space>
                                  <Avatar style={{ backgroundColor: "#1677ff", fontSize: 12 }}>
                                    {item.nome?.charAt(0)}
                                  </Avatar>
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{item.nome}</div>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                      {formatHoras(item.horas_por_user)} ×{" "}
                                      {item.salario_hora ? formatMoney(parseFloat(item.salario_hora)) : "sem salário"}
                                    </Text>
                                    <div style={{ fontWeight: 700, color: "#1677ff", fontSize: 13, marginTop: 2 }}>
                                      {formatMoney(item.custo)}
                                    </div>
                                  </div>
                                </Space>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    );
                  },
                }}
                summary={(pageData) => {
                  const total = pageData.reduce((a, r) => a + r.totalCusto, 0);
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "#f0f9ff" }}>
                        <Table.Summary.Cell index={0} colSpan={5}>
                          <Text strong>Total da página</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong style={{ color: "#1677ff", fontSize: 15 }}>
                            {formatMoney(total)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Card>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default CustoProjeto;